import type { AxiosInstance } from "axios";
import { apiClient, internalApiClient } from "@/marketing/infrastructure/http/httpClient";
import type {
  AddCommentRequest,
  CampaignActivityLog,
  EditCommentRequest,
  TimelineQueryOptions,
} from "@/marketing/models/campaignActivity";
import { pathSegment } from "@/marketing/lib/pathSegment";

const BASE_PATH = "/influencer/campaigns";

export interface AddCampaignInfluencerCommentResponse {
  logId: string;
}

export class CampaignActivityAPIClient {
  // Comments are served by internal-api; the timelines are still on the external facade.
  constructor(
    private readonly client: AxiosInstance = apiClient,
    private readonly internalClient: AxiosInstance = internalApiClient,
  ) {}

  public async getCampaignTimeline(
    campaignId: string,
    options: TimelineQueryOptions = {},
  ): Promise<CampaignActivityLog[]> {
    const { data } = await this.client.get<CampaignActivityLog[]>(
      `${BASE_PATH}/${pathSegment(campaignId)}/timeline`,
      { params: options },
    );
    return data;
  }

  public async getCampaignInfluencerTimeline(
    campaignId: string,
    influencerId: string,
    options: TimelineQueryOptions = {},
  ): Promise<CampaignActivityLog[]> {
    const { data } = await this.client.get<CampaignActivityLog[]>(
      `${BASE_PATH}/${pathSegment(campaignId)}/influencers/${pathSegment(influencerId)}/timeline`,
      { params: options },
    );
    return data;
  }

  public async addCampaignInfluencerComment(
    campaignId: string,
    influencerId: string,
    request: AddCommentRequest,
  ): Promise<AddCampaignInfluencerCommentResponse> {
    const { data } =
      await this.internalClient.post<AddCampaignInfluencerCommentResponse>(
        `${BASE_PATH}/${pathSegment(campaignId)}/influencers/${pathSegment(influencerId)}/comments`,
        request,
      );
    return data;
  }

  public async editCampaignInfluencerComment(
    campaignId: string,
    influencerId: string,
    logId: string,
    request: EditCommentRequest,
  ): Promise<void> {
    await this.internalClient.patch<void>(
      `${BASE_PATH}/${pathSegment(campaignId)}/influencers/${pathSegment(influencerId)}/comments/${pathSegment(logId)}`,
      request,
    );
  }

  public async deleteCampaignInfluencerComment(
    campaignId: string,
    influencerId: string,
    logId: string,
    editorId: string,
  ): Promise<void> {
    await this.internalClient.delete<void>(
      `${BASE_PATH}/${pathSegment(campaignId)}/influencers/${pathSegment(influencerId)}/comments/${pathSegment(logId)}`,
      { data: { editorId } },
    );
  }
}

export const campaignActivityAPIClient = new CampaignActivityAPIClient();
